export type PlayerStatus = "NORMAL" | "PRONE" | "STUNNED" | "KNOCKED_OUT" | "INCAPACITATED";

export interface ClientPlayerInfo {
  playerId: string;
  label: string;
  teamId: string;
  role: string;
  mov: number;
  str: number;
  dex: number;
  skills: string[];
}

export class LocalGameState {
  phase = "PREGAME";
  gameClock = 0;
  homeTeamId: string | null = null;
  awayTeamId: string | null = null;
  homeScore = 0;
  awayScore = 0;
  downNumber = 0;
  downMax = 3;
  playersPerDown = 5;
  maxPassRange = 5;
  lineOfScrimmage: number | null = null;
  firstDownLine: number | null = null;
  offenseTeamId: string | null = null;
  bombTracker = 1;

  /** True when the offensive team is advancing toward higher y (home offense by default). */
  get offenseGoingNorth(): boolean {
    return this.offenseTeamId !== null && this.offenseTeamId === this.homeTeamId;
  }

  fieldWidth = 9;
  fieldHeight = 24;
  endZoneDepth = 2;
  outOfBoundsDepth = 1;

  players: Map<string, ClientPlayerInfo> = new Map();
  homePlayerIds: string[] = [];
  awayPlayerIds: string[] = [];
  playerNames: Map<string, string> = new Map();
  labelToPlayerId: Map<string, string> = new Map();
  playerIdToLabel: Map<string, string> = new Map();

  playerPositions: Map<string, [number, number]> = new Map();
  playerStatus: Map<string, PlayerStatus> = new Map();
  playerInjuries: Map<string, string> = new Map(); // Stores specific injury types like "CONCUSSED", "STUNNED", etc.
  movementRemaining: Map<string, number> = new Map();
  lockedGroups: Set<string>[] = [];
  lockedPairs: [string, string][] = []; // direct attacker-defender lock-up pairs
  activatedThisRound: Set<string> = new Set();
  hasAttacked: Set<string> = new Set();
  hasActedThisActivation: Set<string> = new Set();
  ballCarrierId: string | null = null;
  ballPosition: [number, number] | null = null;

  /** Movement paths collected during the current event batch, for animation. playerId → [startPos, ...steps] */
  pendingMovementPaths: Map<string, Array<[number, number]>> = new Map();

  get playZoneStart(): number {
    return this.endZoneDepth;
  }

  get playZoneEnd(): number {
    return this.fieldHeight - this.endZoneDepth - 1;
  }

  getPlayerStatus(playerId: string): PlayerStatus {
    return this.playerStatus.get(playerId) ?? "NORMAL";
  }

  getLabel(playerId: string): string {
    if (this.playerIdToLabel.has(playerId)) return this.playerIdToLabel.get(playerId)!;
    const homeIdx = this.homePlayerIds.indexOf(playerId);
    if (homeIdx >= 0) return `H${homeIdx + 1}`;
    const awayIdx = this.awayPlayerIds.indexOf(playerId);
    if (awayIdx >= 0) return `A${awayIdx + 1}`;
    return playerId.slice(0, 8);
  }

  getName(playerId: string): string {
    return this.playerNames.get(playerId) ?? this.getLabel(playerId);
  }

  isHomePlayer(playerId: string): boolean {
    const teamId = this.players.get(playerId)?.teamId;
    if (teamId && this.homeTeamId) return teamId === this.homeTeamId;
    return this.homePlayerIds.includes(playerId);
  }
}
